import { Component } from '@angular/core';
import { IonicPage, NavController, NavParams, Platform } from 'ionic-angular';
import { FieldPositioningService } from '../../../services/field-positioning/field-positioning.service';
import {
  DirectionEnum,
  ensureFieldPositioningLeg,
  FieldPositioningEditor,
  FieldPositioningLegSummaryEditor,
  FieldPositioningLegSummaryPerLegEditor,
  MAX_FIELD_POSITIONING_LEGS,
  normalizeFieldPositioningLegSummary
} from '../../../models/field-positioning/field-positioning.models';
import { ValidationResult } from '../../../models/projects/validation.models';
import { AppData } from '../../../models/app-data/app-data';
import { LockedDayService } from '../../../services/locked-day/locked-day.service';
import { AppDataSyncService } from '../../../services/app-data-sync/app-data-sync.service';
import { DateService } from '../../../services/date/date.service';
import { FieldPositioningCompletePage } from '../field-positioning-complete/field-positioning-complete';
import { JackingPermissions, LockedDayPermissions } from '../../../models/permissions/permission.models';
import { PermissionsService } from '../../../services/permissions/permissions.service';
import { TimeZoneService } from '../../../services/time-zone/time-zone.service';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { AppConfig } from '../../../app/app.config';

@IonicPage()
@Component({
  selector: 'app-page-field-positioning-edit',
  templateUrl: 'field-positioning-edit.html',
})
export class FieldPositioningEditPage {
  config = AppConfig.settings;
  readonly maxLegs = MAX_FIELD_POSITIONING_LEGS;

  editor: FieldPositioningEditor;
  loading: boolean;
  isWeb: boolean = false;
  mask: Array<string | RegExp> = [/[0-2]/, /\d/, ':', /[0-5]/, /\d/];

  dailyLogDate: PtLuxonDate;
  isReadonly: boolean;
  isSaving: boolean = false;
  validationResult: ValidationResult;

  permissions: JackingPermissions = new JackingPermissions();
  lockedDayPermissions: LockedDayPermissions = new LockedDayPermissions();

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public platform: Platform,
    public dateService: DateService,
    public lockedDayService: LockedDayService,
    public appDataSyncService: AppDataSyncService,
    private permissionService: PermissionsService,
    public fieldPositioningService: FieldPositioningService,
  ) {
    this.dailyLogDate = this.navParams.get('dailyLogDate');
  }

  ngOnInit() {
    this.loading = true;
    this.isWeb = this.platform.is('core');
    this.ionViewEnter();
  }

  async ionViewEnter() {
    this.editor = this.navParams.get('activity');
    this.normalizeEditorLegInfo();

    this.permissions = await this.permissionService.jackingPermissions();
    this.lockedDayPermissions = await this.permissionService.lockedDayPermissions(this.dailyLogDate);

    // if jacking v2 - all fields are set on the jacking form now - make all readonly
    this.isReadonly = !this.lockedDayPermissions.functionalPermissions.canImpactCurrentLockedDay
      || !!this.editor.jackingFormInstanceId;

    this.loading = false;
  }

  cancel() {
    this.navCtrl.pop();
  }

  saveInProgress() {
    this.editor.confirmed = false;
    this.editor.status = 'Incomplete';
    this.Save();
  }

  private async Save() {
    await this.saveAppData();
    this.navCtrl.pop();
  }

  private async saveAppData() {
    if (!this.isSaving) {
      this.isSaving = true;

      if (this.editor.showPinnedTime) {
        const date = await this.dateService.setPtLuxonDateFromUserEnteredString(this.editor.showPinnedTime, this.editor.startDate.dateTime);
        this.editor.pinnedTime.dateTime = date.dateTime;
      } else {
        this.editor.pinnedTime = null;
      }

      const appDataBlob = new AppData();
      appDataBlob.cleanseLogs = false;
      appDataBlob.fieldPositioningEditors = [this.editor];

      await this.appDataSyncService.syncLocalData(appDataBlob);
    }
  }

  // Save Functions
  async complete() {
    const validation = await this.fieldPositioningService.CalculateMissingFields(this.editor);
    if (validation.isValid) {
      this.editor.confirmed = true;
      this.editor.status = 'Complete';
      this.Save();
    } else {
      const _self = this;
      const saveConfirmCallback = function (editor: FieldPositioningEditor, isCancel: boolean) {
        return new Promise((resolve, reject) => {
          if (isCancel) {
            resolve(null);
          } else {
            _self.editor = editor;
            _self.editor.confirmed = true;
            _self.editor.status = 'Complete';
            _self.Save();
            resolve(null);
          }
        });
      };

      this.navCtrl.push(
        FieldPositioningCompletePage,
        {activity: this.editor, validation: validation, callback: saveConfirmCallback},
        {animation: 'ios-transition'}
      );
    }
  }

  public async pinnedTimeChange(offsetSelected?: boolean, offsetSelectedPtLuxonDate?: PtLuxonDate) {
    const allowedTime = await this.dateService.timeAllowed(
      this.editor.showPinnedTime,
      this.editor.pinnedTime.dateTime
        ? this.editor.pinnedTime.dateTime
        : this.dailyLogDate.dateTime
    );

    if (allowedTime && this.editor.showPinnedTime) {
      if (!offsetSelected) {
        this.editor.pinnedTime = await this.dateService.setPtLuxonDateFromUserEnteredString(
          this.editor.showPinnedTime,
          this.editor.pinnedTime && this.editor.pinnedTime.dateTime
            ? this.editor.pinnedTime.dateTime
            : this.dailyLogDate.dateTime
        );
      } else {

        // todo - check and delete - why is there no pinned time offset?
        // this.editor.pinnedTime = await this.dateService.setDateTimeUsingSelectedOffset(
        //   this.editor.showPinnedTime,
        //   this.editor.pinnedTime && this.editor.pinnedTime.localDate
        //     ? this.editor.pinnedTime.localDate
        //     : this.dailyLogDate.localDate,
        //   offsetSelectedPtLuxonDate.inAppOffset,
        //   offsetSelectedPtLuxonDate.multipleOffsets
        // );
        //
        // this.editor.pinnedTime.inAppOffset = offsetSelectedPtLuxonDate.inAppOffset;
        // this.editor.pinnedTime.multipleOffsets = offsetSelectedPtLuxonDate.multipleOffsets;
        // this.editor.pinnedTimeOffset = offsetSelectedPtLuxonDate.inAppOffset / 60 / 60;
      }

      // stop processing if user has entered a time that has multiple offsets - this method will rerun after they select the offset
      if (this.editor.endDate.offsetNull()) {
        return;
      }

      // if (!offsetSelected) {
      //   this.editor.pinnedTimeOffset = await this.timeZoneService.offsetForTime(
      //     this.editor.pinnedTime.getDate()
      //   );
      // }
    }
  }


  regexChange(inputTime: string) {
    if (inputTime.charAt(0) === '2') {
      this.mask[1] = new RegExp('[0-3]');
    } else {
      this.mask[1] = new RegExp('\\d');
    }
  }

  get direction() {
    return DirectionEnum;
  }

  get displayedLegs(): FieldPositioningLegSummaryPerLegEditor[] {
    if (!this.editor?.legInfo?.legs) {
      return [];
    }

    return this.editor.legInfo.legs
      .slice()
      .filter(leg => !!leg && leg.legNumber >= 1 && leg.legNumber <= this.maxLegs)
      .sort((a, b) => a.legNumber - b.legNumber);
  }

  trackByLegNumber(index: number, leg: FieldPositioningLegSummaryPerLegEditor) {
    return leg.legNumber;
  }


  degreeCheck(event: any, type: DirectionEnum) {

    if (event.target.value < 0) {
      event.target.value = event.target.value * -1;
    }

    if (event.target.value > 360) {
      event.target.value = event.target.value.slice(0, 3);

      if (event.target.value > 360) {
        event.target.value = 360;
      }
    }

    switch (type) {
      case DirectionEnum.wind:
        this.editor.windDirection = event.target.value;
        break;
      case DirectionEnum.heading:
        this.editor.heading = event.target.value;
        break;
      case DirectionEnum.tide:
        this.editor.tideDirection = event.target.value;
        break;
      case DirectionEnum.sea:
        this.editor.seaDirection = event.target.value;
        break;

    }
  }

  private normalizeEditorLegInfo() {
    if (!this.editor.legInfo) {
      this.editor.legInfo = new FieldPositioningLegSummaryEditor();
    }

    normalizeFieldPositioningLegSummary(this.editor.legInfo, this.maxLegs);

    for (let legNumber = 1; legNumber <= Math.min(4, this.maxLegs); legNumber++) {
      ensureFieldPositioningLeg(this.editor.legInfo, legNumber);
    }
  }
}



// WEBPACK FOOTER //
// ./src/pages/field-positioning/field-positioning-edit/field-positioning-edit.ts